import * as Yup from 'yup';

import { SubscriptionTypes } from './subscription.types';
import { LatLng } from '../components/event-map';
import { inviteCustomerFormSchema } from '../schemas/customer.schema';

export namespace CustomerTypes {
    export interface SsoConfig {
        active: boolean,
        clientId: string,
        ssoUrl: string,
    }

    export interface Model {
        id: string;
        city: string;
        name: string;
        contactEmail: string;
        phoneNumber: string;
        contactFirstName?: string;
        contactLastName?: string;
        state: string;
        zip: string | null;
        createdAt: Date | null;
        subscriptionId: string;
        invitation?: Invite;
        homePosition?: LatLng;
        workPosition?: LatLng;
        localPosition?: LatLng;
        ssoConfig?: SsoConfig;
        settings: Settings;
        invites: Invite[];
        subscription: SubscriptionTypes.Model;
    }

    export interface Invite {
        id: string;
        email: string;
        status: OnboardingInvitationStatus;
        customerId: string;
        openedAt: Date | null;
        expiresAt: Date | null;
        createdAt: Date | null;
    }

    export enum OnboardingInvitationStatus {
        AWAITING = 'AWAITING',
        OPENED = 'OPENED',
        DONE = 'DONE',
        ERRORED = 'ERRORED',
    }

    export type Filters = Pick<Model, 'city' | 'name' | 'state' | 'zip'>;

    export interface EmergencyContact {
        contactFirstName: string | null;
        contactLastName: string | null;
        contactPhoneNumber: string | null;
    }

    export type CreateCustomerParams = Yup.InferType<typeof inviteCustomerFormSchema>;

    export interface CreateCustomerResponse {
        customer: CustomerTypes.Model;
        invite: string;
    }

    export interface ImportUser {
        fullname: string,
        email: string,
        role?: Record<string, string>,
        teams?: string,
        groups?: string,
    }

    export interface OnboardingForm {
        companyName: string;
        invitationId: string;
        contactFirstName: string;
        contactLastName: string;
        // Only collected on the invite-acceptance form; the Yup schema there
        // enforces it at runtime. Optional so the many admin screens reusing
        // this type don't have to carry it.
        tempPassword?: string;
        streetAddress?: string;
        stateAddress?: string;
        zipcodeAddress?: number;
        cityAddress?: string;
        phoneOwnership?: 'personal' | 'company';
        phoneOperationSystems?: Array<string>;
        passCode?: string;
        reportsRetention?: number;
        alertsRetention?: number;
        mapSettingsDeviceEnabled?: boolean;
        safetyReportEnabled?: boolean;
        escalateCheckOnMe?: boolean;
        checkOnMeRiskReportSurvey?: boolean;
        checkOnMeRiskReportQuestion?: boolean;
        checkOnMeRiskReportSurveyOverride?: boolean;
        checkOnMeEmergencyContacts?: boolean;
        checkOnMeDispatchEnabled?: boolean;
        proximityToReportsNotification?: boolean;
        locationSettings?: boolean;
        checkOnMeConfig?: string;
        checkOnMeNotifications: string;
        escalateCheckOnMeTimeout?: number;
        escalateShift?: boolean;
        escalateShiftTimeout?: number;
        shiftDispatchEnabled?: boolean;
        shiftConfig?: string;
        showInsightsDashboard?: boolean;
        isActivityReportEnabled?: boolean;
        isSafetyReportEnabled?: boolean;
        showMessageCenter?: boolean;
        showRiskAssessment?: boolean;
        showRiskAssessmentAddressOnly?: boolean;
        userUniqueIdentifier?: string;
        activityStatusFiltersEnabled?: boolean;
        showGroupsTeamsDashboard?: boolean;
        riskReviewScoreThreshold?: number;
        minAppVersion?: string;
        ssoConfig?: SsoConfig;
        assetManagementEnabled?: boolean;
        programReportEnabled?: boolean;
        reportsButtonEnabled?: boolean;
        addReportButtonEnabled?: boolean;
        showAppMessageTab?: boolean;
        pushNotificationPromptEnabled?: boolean;
        appUpdatePromptEnabled?: boolean;
        appointmentsCheckOnMeTrackingEnabled?: boolean;
        hideLastPing?: boolean;
        callOnDispatch?: boolean;
        callOnDispatchPhoneNumbers?: string[];
        callOnDispatchEmails?: string[];
        expireNotifyMethod?: 'MESSAGE' | 'CALL';
        checkoutReminderMessage?: boolean;
        // POM master only. Surfaced on the customer-edit screen for POM
        // masters; not present on the customer self-settings screen.
        pomTriggerEnabled?: boolean;
    }

    export interface GetCustomerDetailsResponse {
        subscriptions: SubscriptionTypes.Model[];
        customer: CustomerTypes.Model;
    }

    /** POST /customer/changeLicenseCount result (POM Master only). */
    export interface ChangeLicenseCountResult {
        subscriptionId: string;
        licenseCount: number;
        previousLicenseCount: number;
        licensesConsumed: number;
    }

    export interface Session {
        id: string;

        customerWebAppUserId: string;
        value: string;
        provider: string;
        expiresAt: string;
        createdAt: string;
    }

    export interface Settings {
        alertSoundUrl: string;
        escalateCheckOnMe: boolean;
        escalateCheckOnMeTimeout: number;
        safetyReportEnabled: boolean;
        checkOnMeRiskReportSurvey: boolean;
        checkOnMeRiskReportQuestion: boolean;
        checkOnMeRiskReportSurveyOverride: boolean;
        checkOnMeEmergencyContacts: boolean;
        checkOnMeDispatchEnabled: boolean;
        checkOnMeConfig: "Enabled" | "Disabled";
        showInsightsDashboard: boolean | undefined;
        showRiskAssessment: boolean | undefined;
        showRiskAssessmentAddressOnly: boolean | undefined;
        isActivityReportEnabled: boolean | undefined;
        isSafetyReportEnabled: boolean | undefined;
        showMessageCenter: boolean | undefined;
        activityStatusFiltersEnabled: boolean | undefined;
        showGroupsTeamsDashboard: boolean | undefined;
        assetManagementEnabled: boolean | undefined;
        programReportEnabled: boolean | undefined;
        reportsButtonEnabled: boolean | undefined;
        addReportButtonEnabled: boolean | undefined;
        showAppMessageTab: boolean | undefined;
        pushNotificationPromptEnabled: boolean | undefined;
        appUpdatePromptEnabled: boolean | undefined;
        appointmentsCheckOnMeTrackingEnabled: boolean | undefined;
        hideLastPing: boolean | undefined;
        callOnDispatch: boolean | undefined;
        callOnDispatchPhoneNumbers: string[] | undefined;
        callOnDispatchEmails: string[] | undefined;
        expireNotifyMethod: 'MESSAGE' | 'CALL' | undefined;
        checkoutReminderMessage: boolean | undefined;
        pomTriggerEnabled: boolean | undefined;
    }

    export type RapidSosRegistryUriType = 'user-id' | 'phone';
    export type RapidSosRegistryStatus = 'active' | 'dormant' | 'user-not-found';

    export interface RapidSosRegistrySummary {
        registered: number;
        estimatedMonthlyCostUsd: number;
        userNotFound: number;
    }

    export interface RapidSosRegistryItem {
        uri: string;
        uriType: RapidSosRegistryUriType;
        userId: string;
        subscriptionId: string;
        name: string | null;
        phoneNumber: string | null;
        employeeId: string | null;
        registeredAt: string | null;
        registeredAtIsEstimate: boolean;
        lastActivityAt: string | null;
        status: RapidSosRegistryStatus;
    }

    export interface RapidSosRegistryResponse {
        summary: RapidSosRegistrySummary;
        items: RapidSosRegistryItem[];
    }
}
